import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../services/api.ts';
import { formatStatus } from '../utils/statusFormatter.ts';

interface Debt {
  id: string;
  creditorName: string;
  description: string;
  originalAmount: number;
  currentAmount: number;
  interestRate: number;
  dueDate: string;
  status: 'active' | 'paid' | 'overdue' | 'renegotiated';
  category:
    | 'loan'
    | 'financing'
    | 'account_payable'
    | 'consortium'
    | 'real_estate'
    | 'vehicle'
    | 'other';
  paymentMethod?: string;
  installments?: number;
  currentInstallment?: number;
  createdAt: string;
  updatedAt: string;
}

const DebtDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const {
    data: debt,
    isLoading,
    error,
  } = useQuery<Debt>(
    ['debt', id],
    async () => {
      const response = await api.get(`/debts/${id}`);
      return response.data;
    },
    {
      enabled: !!id,
    }
  );

  const deleteDebtMutation = useMutation(
    async () => {
      await api.delete(`/debts/${id}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('debts');
        navigate('/debts');
      },
      onError: (error: any) => {
        console.error('Erro ao excluir dívida:', error);
      },
    }
  );

  const handleDelete = () => {
    deleteDebtMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !debt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Dívida não encontrada</h3>
          <p className="text-gray-500 mb-6">
            A dívida solicitada não foi encontrada ou não existe.
          </p>
          <button
            onClick={() => navigate('/debts')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Voltar para Dívidas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{debt.creditorName}</h1>
          <p className="text-gray-600 mt-2">{debt.description}</p>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Informações da Dívida</h2>
          </div>

          <div className="px-6 py-4 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Valor Original
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  R$ {debt.originalAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Saldo Devedor
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  R$ {debt.currentAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Taxa de Juros
                </label>
                <p className="text-lg font-semibold text-gray-900">{debt.interestRate}% ao mês</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Data de Vencimento
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {new Date(debt.dueDate).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                <p className="text-lg font-semibold text-gray-900">{formatStatus(debt.status)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Categoria</label>
                <p className="text-lg font-semibold text-gray-900">
                  {debt.category === 'loan'
                    ? 'Empréstimo'
                    : debt.category === 'financing'
                      ? 'Financiamento'
                      : debt.category === 'account_payable'
                        ? 'Contas a Pagar'
                        : debt.category === 'consortium'
                          ? 'Consórcio'
                          : debt.category === 'real_estate'
                            ? 'Imóvel'
                            : debt.category === 'vehicle'
                              ? 'Veículo'
                              : 'Outros'}
                </p>
              </div>
            </div>

            {debt.paymentMethod && (
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">
                  Método de Pagamento
                </label>
                <p className="text-lg font-semibold text-gray-900">{debt.paymentMethod}</p>
              </div>
            )}

            {debt.installments && debt.installments > 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Parcelas</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {debt.currentInstallment || 1} de {debt.installments}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Progresso</label>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{
                        width: `${((debt.currentInstallment || 1) / debt.installments) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Ações</h2>
          </div>
          <div className="px-6 py-4">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate(`/debts/${id}/edit`)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Editar
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Excluir
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Histórico</h2>
          </div>
          <div className="px-6 py-4">
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Criado em:</span>
                <span className="font-medium">
                  {new Date(debt.createdAt).toLocaleDateString('pt-BR')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Última atualização:</span>
                <span className="font-medium">
                  {new Date(debt.updatedAt).toLocaleDateString('pt-BR')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar Exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir esta dívida? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteDebtMutation.isLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteDebtMutation.isLoading ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebtDetail;
